import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@clerk/react";
import type { SlotVersion, SlotKind } from "scantling-common";
import { useApi } from "./components.js";
import { getSlotComponent } from "./slot_registry.js";
import { SpinnerGap } from "@phosphor-icons/react";

interface SlotEditorProps {
  slot: SlotVersion;
  itemSlug: string;
  onSave?: () => void;
}

function arraysEqual(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

function uint8ArrayToBase64(bytes: Uint8Array): string {
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function base64ToUint8Array(base64: string): Uint8Array {
  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
}

export function SlotEditor({ slot, itemSlug, onSave }: SlotEditorProps) {
  const { getToken } = useAuth();
  const pool = useApi();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [originalContent, setOriginalContent] = useState<Uint8Array | null>(
    null,
  );
  const [content, setContent] = useState<Uint8Array>(new Uint8Array());

  const containerRef = useRef<HTMLDivElement>(null);

  const isModified =
    originalContent !== null && !arraysEqual(originalContent, content);

  // Load blob content
  useEffect(() => {
    const loadContent = async () => {
      if (!slot.blobSlug) {
        const empty = new Uint8Array();
        setOriginalContent(empty);
        setContent(empty);
        setLoading(false);
        return;
      }

      setLoading(true);
      const api = pool.acquire();
      try {
        const token = await getToken();
        if (!token) {
          setError("Not authenticated");
          return;
        }
        const authedApi = await api.authenticate(token);
        const orgApi = authedApi.org;
        if (!orgApi) {
          setError("No org available");
          return;
        }

        const blobBase64 = await orgApi.getBlob(slot.blobSlug);
        if (blobBase64) {
          const data = base64ToUint8Array(blobBase64);
          setOriginalContent(data);
          setContent(data);
        } else {
          setError("Blob not found");
        }
      } catch (err) {
        setError(`${err}`);
      } finally {
        pool.release();
        setLoading(false);
      }
    };

    loadContent();
  }, [slot.blobSlug, getToken, pool]);

  const save = useCallback(async () => {
    if (!isModified || saving) return;

    setSaving(true);
    const api = pool.acquire();
    try {
      const token = await getToken();
      if (!token) throw new Error("Not authenticated");

      const authedApi = await api.authenticate(token);
      const orgApi = authedApi.org;
      if (!orgApi) throw new Error("No org available");

      const blobSlug = await orgApi.createBlob(uint8ArrayToBase64(content));

      const result = await orgApi.executeAction({
        verb: "updateSlotBlob",
        params: { itemSlug, slotName: slot.name, blobSlug },
      });

      if (!result.success) {
        throw new Error(result.error ?? "Failed to update slot");
      }

      setOriginalContent(content);
      onSave?.();
    } catch (err) {
      setError(`Save failed: ${err}`);
    } finally {
      pool.release();
      setSaving(false);
    }
  }, [
    isModified,
    saving,
    content,
    itemSlug,
    slot.name,
    getToken,
    pool,
    onSave,
  ]);

  const handleFocusOut = useCallback(
    (e: React.FocusEvent) => {
      // Only save if focus left the container entirely
      if (
        containerRef.current &&
        !containerRef.current.contains(e.relatedTarget as Node)
      ) {
        if (isModified) {
          save();
        }
      }
    },
    [isModified, save],
  );

  const update = useCallback((newContent: Uint8Array) => {
    setContent(newContent);
  }, []);

  // Save immediately with provided content (for components like image that don't have blur behavior)
  const saveImmediately = useCallback(
    async (newContent: Uint8Array) => {
      if (saving) return;

      setSaving(true);
      const api = pool.acquire();
      try {
        const token = await getToken();
        if (!token) throw new Error("Not authenticated");

        const authedApi = await api.authenticate(token);
        const orgApi = authedApi.org;
        if (!orgApi) throw new Error("No org available");

        const blobSlug = await orgApi.createBlob(
          uint8ArrayToBase64(newContent),
        );

        const result = await orgApi.executeAction({
          verb: "updateSlotBlob",
          params: { itemSlug, slotName: slot.name, blobSlug },
        });

        if (!result.success) {
          throw new Error(result.error ?? "Failed to update slot");
        }

        setOriginalContent(newContent);
        setContent(newContent);
        onSave?.();
      } catch (err) {
        setError(`Save failed: ${err}`);
      } finally {
        pool.release();
        setSaving(false);
      }
    },
    [saving, itemSlug, slot.name, getToken, pool, onSave],
  );

  const SlotComponent = getSlotComponent(slot.kind as SlotKind);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground">
        <SpinnerGap className="animate-spin" size={16} />
        <span className="text-sm">Loading...</span>
      </div>
    );
  }

  if (error) {
    return <div className="text-sm text-destructive">{error}</div>;
  }

  if (!SlotComponent) {
    return (
      <div className="text-sm text-muted-foreground">
        No editor for kind: {slot.kind}
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative" onBlur={handleFocusOut}>
      {(isModified || saving) && (
        <span className="absolute -top-2 right-0 text-xs text-muted-foreground">
          {saving ? "Saving..." : "modified"}
        </span>
      )}
      <SlotComponent
        content={content}
        update={update}
        saveImmediately={saveImmediately}
      />
    </div>
  );
}
